/**
 * Financial status "reality check" bands.
 *
 * An illustrative income-ladder framework mapping annual USD income to a
 * status label plus approximate global and US population shares. This is NOT
 * an official Census/BLS/World Bank statistical series - it is a simplified
 * educational framework layered on top of Wage Atlas's official OEWS data.
 *
 * Source table uses monthly income for the lower tiers; those are stored here
 * annualized (monthly x 12). Millionaire+ tiers are annual as provided.
 */

export interface FinancialStatusBand {
    id: string;
    label: string;
    /** Inclusive lower bound, annual USD. */
    minAnnual: number;
    /** Exclusive upper bound, annual USD. Null = open-ended top band. */
    maxAnnual: number | null;
    /** Approximate share of the global population in this band, e.g. "~35%". */
    globalPopShare: string;
    /** Approximate share of the US population in this band, e.g. "~10%". */
    usaPopShare: string;
    /** Human-friendly range for display, e.g. "$1,500 - $3,000/mo". */
    displayRange: string;
    /** True when the source table expressed this band as monthly income. */
    monthlyOrigin: boolean;
}

export const FINANCIAL_STATUS_BANDS: FinancialStatusBand[] = [
    {
        id: "poor",
        label: "Poor",
        minAnnual: 0,
        maxAnnual: 18_000,
        globalPopShare: "~35%",
        usaPopShare: "~10%",
        displayRange: "Under $1,500/mo",
        monthlyOrigin: true,
    },
    {
        id: "low-income",
        label: "Low-income",
        minAnnual: 18_000,
        maxAnnual: 36_000,
        globalPopShare: "~25%",
        usaPopShare: "~18%",
        displayRange: "$1,500 - $3,000/mo",
        monthlyOrigin: true,
    },
    {
        id: "stably-paid",
        label: "Stably paid",
        minAnnual: 36_000,
        maxAnnual: 54_000,
        globalPopShare: "~15%",
        usaPopShare: "~12%",
        displayRange: "$3,000 - $4,500/mo",
        monthlyOrigin: true,
    },
    {
        id: "middle-class",
        label: "Middle class",
        minAnnual: 54_000,
        maxAnnual: 72_000,
        globalPopShare: "~10%",
        usaPopShare: "~10%",
        displayRange: "$4,500 - $6,000/mo",
        monthlyOrigin: true,
    },
    {
        id: "comfortable",
        label: "Comfortable",
        minAnnual: 72_000,
        maxAnnual: 108_000,
        globalPopShare: "~7%",
        usaPopShare: "~12%",
        displayRange: "$6,000 - $9,000/mo",
        monthlyOrigin: true,
    },
    {
        id: "upper-middle-class",
        label: "Upper-middle class",
        minAnnual: 108_000,
        maxAnnual: 144_000,
        globalPopShare: "~4%",
        usaPopShare: "~13%",
        displayRange: "$9,000 - $12,000/mo",
        monthlyOrigin: true,
    },
    {
        id: "rich",
        label: "Rich",
        minAnnual: 144_000,
        maxAnnual: 300_000,
        globalPopShare: "~2.5%",
        usaPopShare: "~18%",
        displayRange: "$12,000 - $25,000/mo",
        monthlyOrigin: true,
    },
    {
        // Source band tops out at $80k/mo ($960k/yr); the small gap up to the
        // Millionaire tier ($1M/yr) is folded into "Wealthy" so the ladder is
        // continuous.
        id: "wealthy",
        label: "Wealthy",
        minAnnual: 300_000,
        maxAnnual: 1_000_000,
        globalPopShare: "~1.2%",
        usaPopShare: "~6.5%",
        displayRange: "$25,000 - $80,000/mo",
        monthlyOrigin: true,
    },
    {
        id: "millionaire",
        label: "Millionaire",
        minAnnual: 1_000_000,
        maxAnnual: 2_000_000,
        globalPopShare: "~0.2%",
        usaPopShare: "~0.3%",
        displayRange: "$1M - $2M/yr",
        monthlyOrigin: false,
    },
    {
        id: "multi-millionaire",
        label: "Multi-millionaire",
        minAnnual: 2_000_000,
        maxAnnual: 30_000_000,
        globalPopShare: "< 0.1%",
        usaPopShare: "~0.1%",
        displayRange: "$2M - $30M/yr",
        monthlyOrigin: false,
    },
    {
        id: "ultra-rich",
        label: "Ultra-rich",
        minAnnual: 30_000_000,
        maxAnnual: 1_000_000_000,
        globalPopShare: "< 0.01%",
        usaPopShare: "< 0.01%",
        displayRange: "$30M - $1B/yr",
        monthlyOrigin: false,
    },
    {
        id: "billionaire",
        label: "Billionaire",
        minAnnual: 1_000_000_000,
        maxAnnual: 2_000_000_000,
        globalPopShare: "~0.00004%",
        usaPopShare: "~0.0003%",
        displayRange: "$1B - $2B/yr",
        monthlyOrigin: false,
    },
    {
        id: "multi-billionaire",
        label: "Multi-billionaire",
        minAnnual: 2_000_000_000,
        maxAnnual: 1_000_000_000_000,
        globalPopShare: "~0.00002%",
        usaPopShare: "~0.0002%",
        displayRange: "More than $2B/yr",
        monthlyOrigin: false,
    },
    {
        id: "trillionaire",
        label: "Trillionaire",
        minAnnual: 1_000_000_000_000,
        maxAnnual: 2_000_000_000_000,
        globalPopShare: "0%",
        usaPopShare: "0%",
        displayRange: "$1T - $2T/yr",
        monthlyOrigin: false,
    },
    {
        id: "multi-trillionaire",
        label: "Multi-trillionaire",
        minAnnual: 2_000_000_000_000,
        maxAnnual: null,
        globalPopShare: "0%",
        usaPopShare: "0%",
        displayRange: "More than $2T/yr",
        monthlyOrigin: false,
    },
];

export interface FinancialStatusResult {
    band: FinancialStatusBand;
    /** Index of the band within FINANCIAL_STATUS_BANDS (0 = Poor). */
    index: number;
    annualUsd: number;
}

/**
 * Maps an annual USD income to its financial status band. Returns null for
 * non-positive or non-finite input.
 */
export function resolveFinancialStatus(
    annualUsd: number,
): FinancialStatusResult | null {
    if (!Number.isFinite(annualUsd) || annualUsd <= 0) return null;

    const index = FINANCIAL_STATUS_BANDS.findIndex(
        (band) =>
            annualUsd >= band.minAnnual &&
            (band.maxAnnual == null || annualUsd < band.maxAnnual),
    );
    if (index === -1) return null;

    return { band: FINANCIAL_STATUS_BANDS[index], index, annualUsd };
}

const compactUsd = (value: number): string =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 0,
    }).format(value);

/**
 * Formats a band's range for UI display. Monthly-origin bands show both the
 * original monthly range and the annual equivalent used for matching.
 */
export function formatBandRange(band: FinancialStatusBand): string {
    if (!band.monthlyOrigin) return band.displayRange;

    const annual = band.maxAnnual == null
        ? `${compactUsd(band.minAnnual)}+/yr`
        : band.minAnnual === 0
        ? `under ${compactUsd(band.maxAnnual)}/yr`
        : `${compactUsd(band.minAnnual)} - ${compactUsd(band.maxAnnual)}/yr`;

    return `${band.displayRange} (${annual})`;
}

/**
 * Short annual-only range for compact table/ladder cells,
 * e.g. "Under $18K", "$72K-$108K", "$2T+".
 */
export function formatAnnualRange(band: FinancialStatusBand): string {
    if (band.maxAnnual == null) return `${compactUsd(band.minAnnual)}+`;
    if (band.minAnnual === 0) return `Under ${compactUsd(band.maxAnnual)}`;
    return `${compactUsd(band.minAnnual)}-${compactUsd(band.maxAnnual)}`;
}
